import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useShallow } from "zustand/react/shallow";
import { useReplayStore } from "@/stores/replayStore";
import { useReplayTransport } from "@/hooks/useReplayTransport";
import { getReplayLoadingMessage } from "@/lib/lytick/replay-utils";

const MIN_VISIBLE_MS = 3000;

const SPINNER = (
  <svg
    className="animate-spin shrink-0"
    width="14"
    height="14"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden
  >
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" />
    <path d="M6 1a5 5 0 0 1 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export function ReplayLoadingOverlay() {
  const { sessionState, loadProgress } = useReplayStore(
    useShallow((s) => ({ sessionState: s.sessionState, loadProgress: s.loadProgress })),
  );
  const { exit } = useReplayTransport();

  // visible stays true for at least MIN_VISIBLE_MS after loading begins
  const [visible, setVisible] = useState(false);
  const startedAtRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (sessionState === "loading") {
      // Loading started — show overlay and record start time
      if (startedAtRef.current === null) {
        startedAtRef.current = Date.now();
      }
      clearTimeout(timerRef.current);
      setVisible(true);
    } else {
      // Loading ended — hide only after the minimum duration has passed
      const elapsed = startedAtRef.current != null ? Date.now() - startedAtRef.current : MIN_VISIBLE_MS;
      const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setVisible(false);
        startedAtRef.current = null;
      }, remaining);
    }
    return () => clearTimeout(timerRef.current);
  }, [sessionState]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="replay-loading-overlay"
          initial={{ opacity: 0, y: -8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.97 }}
          transition={{ duration: 0.18 }}
          aria-live="polite"
          className="fixed z-[100] flex flex-col gap-3 px-5 py-4 rounded-2xl border shadow-[0_8px_40px_rgba(0,0,0,0.55)]"
          style={{
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            minWidth: 260,
            maxWidth: 320,
            background: "color-mix(in srgb, var(--bg-elevated) 92%, transparent)",
            borderColor: "var(--lyt-border-default)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          {/* Phase label + spinner */}
          <div className="flex items-center gap-2.5">
            <span style={{ color: "var(--accent-primary)" }}>{SPINNER}</span>
            <span className="text-sm font-medium text-foreground">
              {getReplayLoadingMessage(loadProgress)}
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <motion.div
              className="h-full rounded-full"
              style={{ background: "var(--gradient-replay)" }}
              initial={{ width: "0%" }}
              animate={{ width: `${loadProgress}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </div>

          {/* Progress % + cancel */}
          <div className="flex items-center justify-between">
            <span className="text-xxs font-mono-tabular text-muted-foreground">
              {loadProgress}%
            </span>
            <button
              type="button"
              onClick={exit}
              className="text-xxs text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
